import { useEffect, useState } from "react";
import { Download, X, Share, Plus } from "lucide-react";

const DISMISS_KEY = "as_install_dismissed";

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true
  );
}
function isIOS() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;
}

/** Floating "Install app" widget. Uses the native install prompt where available,
 *  and shows Add-to-Home-Screen instructions on iOS (which has no prompt API). */
export default function InstallWidget() {
  const [deferred, setDeferred] = useState(null);
  const [open, setOpen] = useState(false);
  const [showIosHelp, setShowIosHelp] = useState(false);

  useEffect(() => {
    if (isStandalone() || sessionStorage.getItem(DISMISS_KEY)) return;
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setOpen(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    // iOS never fires beforeinstallprompt — offer manual instructions.
    if (isIOS()) setOpen(true);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const dismiss = () => {
    setOpen(false);
    setShowIosHelp(false);
    sessionStorage.setItem(DISMISS_KEY, "1");
  };

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      await deferred.userChoice;
      setDeferred(null);
      setOpen(false);
    } else if (isIOS()) {
      setShowIosHelp(true);
    }
  };

  if (!open) return null;

  return (
    <div data-testid="install-widget" className="fixed bottom-4 right-4 z-[60] w-[300px] al-card p-4 shadow-2xl shadow-black/50 border-amber-500/30">
      <button data-testid="install-dismiss" onClick={dismiss} className="absolute top-2 right-2 text-slate-500 hover:text-slate-300"><X className="w-4 h-4" /></button>
      <div className="flex items-start gap-3">
        <img src="/icon-192.png" alt="AuctionScope" className="w-11 h-11 rounded-xl border border-slate-700" />
        <div className="min-w-0">
          <p className="font-serif text-lg leading-tight text-slate-100">Install AuctionScope</p>
          <p className="text-xs text-slate-400 mt-0.5">Add the app to your device for one-tap access and a full-screen experience.</p>
        </div>
      </div>

      {showIosHelp ? (
        <div className="mt-3 text-xs text-slate-300 space-y-1.5 bg-[#0E131F] rounded-md p-3 border border-slate-800">
          <p className="flex items-center gap-2">1. Tap the <Share className="w-3.5 h-3.5 text-amber-400" /> Share button in Safari</p>
          <p className="flex items-center gap-2">2. Choose <Plus className="w-3.5 h-3.5 text-amber-400" /> “Add to Home Screen”</p>
          <p>3. Tap “Add” — done.</p>
        </div>
      ) : (
        <button data-testid="install-button" onClick={install} className="al-btn w-full mt-3">
          <Download className="w-4 h-4" /> {isIOS() ? "How to install" : "Install app"}
        </button>
      )}
    </div>
  );
}
